import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { Helmet } from "react-helmet-async";

const PROD_DOMAIN = "https://www.thesecretseller.co.uk";

export default function LandingStagedSellFasterPage() {
  return (
    <main data-testid="landing-sell-faster" className="pt-24">
      <Helmet>
        <title>Do Virtually Staged Properties Sell Faster? | The Secret Seller</title>
        <meta name="description" content="Research shows virtually staged properties sell up to 73% faster with 40% more portal clicks. See the evidence and real results from UK estate agents." />
        <link rel="canonical" href={`${PROD_DOMAIN}/do-staged-properties-sell-faster`} />
      </Helmet>

      <section className="py-20 lg:py-28 bg-[#FAFAF8]">
        <div className="max-w-3xl mx-auto px-8 lg:px-16 text-center">
          <p className="uppercase text-[11px] tracking-[0.3em] text-[#758C7B] mb-4 font-medium">The Evidence</p>
          <h1 className="font-['Outfit'] text-3xl sm:text-4xl lg:text-[2.8rem] font-extralight text-[#0D2D54] leading-[1.1] tracking-[-0.01em] mb-8">
            Do virtually staged properties sell faster?
          </h1>
          <p className="text-[15px] text-[#6B7280] leading-[1.8]">The short answer: yes. Here's the data behind virtual staging and what it means for your listings.</p>
        </div>
      </section>

      <section className="py-24 lg:py-32">
        <div className="max-w-3xl mx-auto px-8 lg:px-16 space-y-12">
          <div>
            <h2 className="font-['Outfit'] text-xl lg:text-2xl font-extralight text-[#0D2D54] mb-4">The numbers</h2>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 my-8">
              {[
                { stat: "73%", label: "faster time to offer" },
                { stat: "40%", label: "more clicks on portals" },
                { stat: "20%", label: "higher perceived value" },
              ].map((item, i) => (
                <div key={i} className="text-center bg-[#FAFAF8] p-6 border border-[#E8E8E4]">
                  <p className="font-['Outfit'] text-3xl font-extralight text-[#0D2D54]">{item.stat}</p>
                  <p className="text-xs text-[#6B7280] mt-1">{item.label}</p>
                </div>
              ))}
            </div>
            <p className="text-[15px] text-[#6B7280] leading-[1.8]">These figures are drawn from estate agency data across the UK and US markets. The consistent finding: properties marketed with staged imagery outperform empty equivalents on every measure that matters — clicks, viewings, time to offer and final sale price.</p>
          </div>

          <div>
            <h2 className="font-['Outfit'] text-xl lg:text-2xl font-extralight text-[#0D2D54] mb-4">Why it works: the psychology</h2>
            <p className="text-[15px] text-[#6B7280] leading-[1.8]">Buyers make emotional decisions. An empty room triggers nothing — it's just walls and flooring. A staged room tells a story: this is where you'll have morning coffee, this is where the children will play, this is where you'll relax after work. That emotional connection translates directly into faster decisions and stronger offers.</p>
          </div>

          <div>
            <h2 className="font-['Outfit'] text-xl lg:text-2xl font-extralight text-[#0D2D54] mb-4">The Rightmove factor</h2>
            <p className="text-[15px] text-[#6B7280] leading-[1.8]">Rightmove's own data shows that listings with high-quality images receive significantly more engagement. In a market where buyers scroll through dozens of listings, your photos have about two seconds to make an impression. Virtually staged photos stop the scroll. Empty rooms don't.</p>
          </div>

          <div>
            <h2 className="font-['Outfit'] text-xl lg:text-2xl font-extralight text-[#0D2D54] mb-4">The cost versus return</h2>
            <p className="text-[15px] text-[#6B7280] leading-[1.8] mb-4">Virtual staging: 6 images at £55 = £330. A faster sale by even two weeks saves £1,000+ in mortgage or holding costs. A higher offer by just 1% on a £300,000 property = £3,000. The return on investment is significant.</p>
          </div>
        </div>
      </section>

      <section className="py-24 lg:py-32 bg-[#0D2D54]">
        <div className="max-w-3xl mx-auto px-8 lg:px-16 text-center">
          <h2 className="font-['Outfit'] text-2xl lg:text-[2rem] font-extralight text-white mb-6">See the difference for yourself</h2>
          <p className="text-[15px] text-white/50 mb-10">Send us your photos and we'll show you what virtual staging can do for your listings.</p>
          <Link to="/order" className="inline-flex items-center gap-3 bg-white text-[#0D2D54] px-10 py-4 text-[13px] font-medium uppercase tracking-[0.15em] hover:bg-white/90 transition-all">
            Send Us Your Photos <ArrowRight size={14} />
          </Link>
        </div>
      </section>
    </main>
  );
}
